/**
 * ResponseSummaryStrip component
 *
 * Compact horizontal metrics bar displaying Issue Age, Time to First
 * Response, Status Duration, and Activity Count. Fetches its own
 * page-1 activity data so metrics remain stable as the user paginates
 * through the timeline.
 */

import { useMemo } from "react";
import type { SecurityIssue } from "@/types/security";
import { useIssueActivities } from "@/hooks/useIssueActivities";
import { formatDuration } from "@/utils/securityHelpers";
import { STATUS_LABELS, STATUS_DOT } from "@/constants/severity";

interface ResponseSummaryStripProps {
  issue: SecurityIssue;
  numericIssueId: number;
}

/** Activity types that represent a human response (count as first response). */
const HUMAN_ACTIVITY_TYPES = new Set([
  "STATUS_CHANGED",
  "ASSIGNED",
  "COMMENT_ADDED",
  "COMMENT_EDITED",
  "ACTION_EXECUTED",
  "TICKET_CREATED",
  "SUPPRESSION_CREATED",
  "EXPORTED",
  "TRACE_LINKED",
]);

function safeParseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d;
}

const DASH = "\u2014";

export function ResponseSummaryStrip({
  issue,
  numericIssueId,
}: ResponseSummaryStripProps) {
  // Fetch page 1 independently — React Query dedupes with the same
  // cache key ["issue-activities", id, 1] used by HistoryTab on page 1.
  const { data, isLoading, isError } = useIssueActivities(numericIssueId, 1);

  const activities = data?.data ?? [];
  const totalCount = data?.total_count ?? 0;
  const hasMore = data?.has_more ?? false;

  // ── 1. Issue Age ─────────────────────────────────────────────────────
  const issueAge = useMemo(() => {
    const created = safeParseDate(issue.timestamp);
    if (!created) return DASH;
    const age = Date.now() - created.getTime();
    if (age < 0) return DASH;
    return formatDuration(age);
  }, [issue.timestamp]);

  const isResolved =
    issue.status === "RESOLVED" || issue.status === "FALSE_POSITIVE";

  // ── 2. Time to First Response ────────────────────────────────────────
  const firstResponse = useMemo(() => {
    if (isLoading) return DASH;
    if (isError) return DASH;
    if (activities.length === 0) return "Awaiting triage";

    const issueCreated = safeParseDate(issue.timestamp);
    if (!issueCreated) return DASH;

    if (!hasMore) {
      // We have ALL activities — reverse (newest-first → oldest-first)
      // to find the earliest human activity.
      const reversed = [...activities].reverse();
      const first = reversed.find((a) =>
        HUMAN_ACTIVITY_TYPES.has(a.activity_type),
      );
      if (!first) return "Awaiting triage";

      const firstDate = safeParseDate(first.created_at);
      if (!firstDate) return DASH;

      const diff = firstDate.getTime() - issueCreated.getTime();
      if (diff < 0) return DASH;
      return formatDuration(diff);
    }

    // Paginated — use assigned_at as proxy if available.
    const assignedAt = safeParseDate(issue.assigned_at);
    if (!assignedAt) return DASH;

    const diff = assignedAt.getTime() - issueCreated.getTime();
    if (diff < 0) return DASH;
    return formatDuration(diff);
  }, [isLoading, isError, activities, hasMore, issue.timestamp, issue.assigned_at]);

  const isAwaitingTriage = firstResponse === "Awaiting triage";

  // ── 3. Status Duration ───────────────────────────────────────────────
  const statusDuration = useMemo(() => {
    const statusLabel = STATUS_LABELS[issue.status] ?? issue.status;

    if (isLoading || isError) return statusLabel;

    // Find most recent STATUS_CHANGED (activities are newest-first on page 1).
    const lastStatusChange = activities.find(
      (a) => a.activity_type === "STATUS_CHANGED",
    );

    if (lastStatusChange) {
      const changedAt = safeParseDate(lastStatusChange.created_at);
      if (!changedAt) return statusLabel;

      const elapsed = Date.now() - changedAt.getTime();
      if (elapsed < 0) return statusLabel;

      const formatted = formatDuration(elapsed);

      if (issue.status === "RESOLVED" || issue.status === "FALSE_POSITIVE") {
        return `${statusLabel} ${formatted} ago`;
      }
      return `${statusLabel} for ${formatted}`;
    }

    // No STATUS_CHANGED on page 1.
    if (hasMore) return statusLabel;

    // No STATUS_CHANGED at all — issue has been in current status since creation.
    if (issue.status === "OPEN") {
      const created = safeParseDate(issue.timestamp);
      if (!created) return statusLabel;

      const elapsed = Date.now() - created.getTime();
      if (elapsed < 0) return statusLabel;
      return `${statusLabel} for ${formatDuration(elapsed)}`;
    }

    return statusLabel;
  }, [activities, hasMore, issue.status, issue.timestamp, isLoading, isError]);

  // ── 4. Activity Count ────────────────────────────────────────────────
  const activityCount = useMemo(() => {
    if (isLoading) return DASH;
    if (isError) return DASH;
    return `${totalCount} ${totalCount === 1 ? "event" : "events"}`;
  }, [isLoading, isError, totalCount]);

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <dl className="rounded-lg border bg-card shadow-sm grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border">
      {/* Issue Age */}
      <div className="px-5 py-4">
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Issue Age
        </dt>
        <dd className="text-lg font-semibold tabular-nums text-foreground mt-1">
          {issueAge}
          {isResolved && issueAge !== DASH && (
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              ({issue.status === "FALSE_POSITIVE" ? "closed" : "resolved"})
            </span>
          )}
        </dd>
      </div>

      {/* Time to First Response */}
      <div className={`px-5 py-4 ${isAwaitingTriage ? "border-l-2 border-l-amber-400 sm:border-l-0 sm:bg-amber-50/50 dark:sm:bg-amber-950/20" : ""}`}>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          First Response
        </dt>
        <dd
          className={`text-lg font-semibold tabular-nums mt-1 ${
            isAwaitingTriage
              ? "text-amber-600 dark:text-amber-400"
              : "text-foreground"
          }`}
        >
          {firstResponse}
        </dd>
      </div>

      {/* Status Duration */}
      <div className="px-5 py-4">
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Status
        </dt>
        <dd className="text-lg font-semibold text-foreground mt-1 flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full flex-shrink-0 ${
              STATUS_DOT[issue.status] ?? "bg-muted-foreground/50"
            }`}
          />
          <span className="tabular-nums">{statusDuration}</span>
        </dd>
      </div>

      {/* Activity Count */}
      <div className="px-5 py-4">
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Events
        </dt>
        <dd className="text-lg font-semibold tabular-nums text-foreground mt-1">
          {activityCount}
        </dd>
      </div>
    </dl>
  );
}
